import { useLayoutEffect, useState } from 'react';
import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Grid from '@mui/material/Grid';
import LinearProgress from '@mui/material/LinearProgress';
import Paper from '@mui/material/Paper';
import Typography from '@mui/material/Typography';
import QrCodeScannerOutlined from '@mui/icons-material/QrCodeScannerOutlined';
import PortalLayout from '../../../../components/layouts/PortalLayout';
import { theme } from '@omnigenbiodata/ui';
import { useAppDispatch, useAppSelector } from '../../../../store';
import {
  errorSelector,
  isBusySelector,
  linkParticipantAndSampleThunk,
  participantThunk,
  reset,
  responseSelector,
  sampleKitSelector,
  sampleLinkSelector,
  setSampleKit,
} from '../../../../store/participant';
import LinkingSamplePanel from './components/LinkingSamplePanel';
import LinkingFailedPanel from './components/LinkingFailedPanel';
import LinkingSuccessPanel from './components/LinkingSuccessPanel';
import FinalConfirmationPanel from './components/FinalConfirmationPanel';
import ConfirmedParticipantBanner from './components/ConfirmedParticipantBanner';
import ConfirmedSampleBanner from './components/ConfirmedSampleBanner';
import SampleConfirmationPanel from './components/SampleConfirmationPanel';
import ParticipantConfirmationPanel from './components/ParticipantConfirmationPanel';
import PhoneQrIcon from './components/PhoneQrIcon';
import SampleBarcodeIcon from './components/SampleBarcodeIcon';
import SampleBarcodeScannerDialog from './components/SampleBarcodeScannerDialog';
import ParticipantQrScannerDialog from './components/ParticipantQrScannerDialog';

const primaryTint = theme.palette.grey[100];
const totalSteps = 5;

function SampleScene() {
  const dispatch = useAppDispatch();
  const participant = useAppSelector(responseSelector);
  const isBusy = useAppSelector(isBusySelector);
  const sampleLink = useAppSelector(sampleLinkSelector);
  const errors = useAppSelector(errorSelector);

  const [currentStep, setCurrentStep] = useState(1);
  const [participantConfirmed, setParticipantConfirmed] = useState(false);
  const sample = useAppSelector(sampleKitSelector);
  const [scanError, setScanError] = useState<string | null>(null);
  const [isParticipantQrScannerOpen, setParticipantQrScannerOpen] = useState(false);
  const [sampleScanError, setSampleScanError] = useState<string | null>(null);
  const [isSampleScannerOpen, setSampleScannerOpen] = useState(false);
  const [secondSampleBarcode, setSecondSampleBarcode] = useState('');

  useLayoutEffect(() => {
    dispatch(reset());

    setCurrentStep(1);
    setScanError(null);
    setSampleScanError(null);
    setSampleScannerOpen(false);
  }, [dispatch]);

  const progressValue = (currentStep / totalSteps) * 100;

  const handleScanParticipantQr = () => {
    setScanError(null);
    dispatch(reset());
    setParticipantQrScannerOpen(true);
  };

  const handleParticipantQrDetected = (qrPayload: string) => {
    console.log('Participant QR payload:', qrPayload);

    const participantId = qrPayload;

    if (!participantId) {
      setScanError('The QR code was read, but it did not contain a valid participant link.');
      return;
    }

    dispatch(participantThunk(participantId));
  };

  const handleConfirmParticipant = () => {
    setParticipantConfirmed(true);
    setCurrentStep(2);
  };

  const handleScanSampleBarcode = () => {
    setSampleScanError(null);
    setSampleScannerOpen(true);
  };

  const handleSampleBarcodeDetected = (sampleBarcode: string) => {
    if (currentStep === 2) {
      dispatch(
        setSampleKit({
          sampleBarcode,
          linkDate: new Date().toISOString(),
        }),
      );
    }

    if (currentStep === 4) {
      setSecondSampleBarcode(sampleBarcode);
    }
    setSampleScanError(null);
  };

  const handleContinueToFinalConfirmation = () => {
    setCurrentStep(3);
  };

  const handleConfirmSampleAndParticipant = () => {
    setCurrentStep(4);
  };

  const handleDoubleScanAndConfirmation = () => {
    setCurrentStep(5);
    if (participant?.participantId && sample?.sampleBarcode) {
      dispatch(
        linkParticipantAndSampleThunk({
          participantId: participant?.participantId,
          sampleBarcode: sample?.sampleBarcode,
        }),
      );
    }
  };

  const handleStartAgain = () => {
    dispatch(reset());
    setCurrentStep(1);
    setParticipantConfirmed(false);
    setScanError(null);
    setSampleScanError(null);
  };

  const participantLookupError = errors?.[0]?.message;

  const hasParticipantLookupError = Boolean(!isBusy && !participant && !participantConfirmed && participantLookupError);

  const participantErrorMessage = scanError
    ? scanError
    : hasParticipantLookupError
      ? 'No matching participant found. Please check the participant has opened the correct secure Participant Portal and scan the QR code again.'
      : null;

  return (
    <PortalLayout>
      <ParticipantQrScannerDialog
        isOpen={isParticipantQrScannerOpen}
        onClose={() => setParticipantQrScannerOpen(false)}
        onDetected={handleParticipantQrDetected}
      />

      <SampleBarcodeScannerDialog
        isOpen={isSampleScannerOpen}
        onClose={() => setSampleScannerOpen(false)}
        onDetected={handleSampleBarcodeDetected}
      />

      <Box pb={3}>
        <Paper
          elevation={4}
          sx={{
            border: 1,
            borderColor: 'rgba(16, 53, 68, 0.18)',
            bgcolor: primaryTint,
            overflow: 'hidden',
          }}
        >
          <Box sx={{ height: 12, bgcolor: currentStep === 4 ? 'success.main' : 'primary.main' }} />

          <Box p={{ xs: 3, md: 6 }}>
            <Box mb={5}>
              <Box
                sx={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  gap: 2,
                  mb: 1.5,
                }}
              >
                <Typography variant="h6" component="p" sx={{ fontWeight: 700, color: 'primary.main' }}>
                  Sample linking progress
                </Typography>

                <Typography variant="h6" component="p" sx={{ fontWeight: 700, color: 'primary.main' }}>
                  Step {currentStep} of {totalSteps}
                </Typography>
              </Box>

              <LinearProgress
                variant="determinate"
                value={progressValue}
                sx={{
                  height: 14,
                  borderRadius: 999,
                  bgcolor: 'rgba(16, 53, 68, 0.12)',
                  '& .MuiLinearProgress-bar': {
                    borderRadius: 999,
                    bgcolor: 'success.main',
                  },
                }}
              />
            </Box>

            {participantConfirmed && participant && currentStep < 4 && (
              <ConfirmedParticipantBanner participant={participant} />
            )}

            {sample && currentStep === 3 && <ConfirmedSampleBanner sample={sample} />}

            {currentStep === 1 && (
              <>
                <Grid container spacing={4} alignItems="stretch">
                  <Grid item xs={12} md={4}>
                    <Box
                      sx={{
                        height: '100%',
                        minHeight: 320,
                        borderRadius: 4,
                        bgcolor: 'background.paper',
                        border: 1,
                        borderColor: 'rgba(16, 53, 68, 0.18)',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        p: 3,
                      }}
                    >
                      <PhoneQrIcon />
                    </Box>
                  </Grid>

                  <Grid item xs={12} md={8}>
                    <Typography variant="h3" component="h1" sx={{ fontWeight: 700, color: 'primary.main', mb: 2 }}>
                      Step 1: Link Participant
                    </Typography>

                    <Typography
                      variant="h5"
                      component="p"
                      color="text.secondary"
                      sx={{ lineHeight: 1.4, maxWidth: 760 }}
                    >
                      Please scan the participant QR found in the participant&lsquo;s secure Participant Portal.
                    </Typography>

                    <Box sx={{ mt: 4, p: 3, borderRadius: 3, bgcolor: 'info.lighter' }}>
                      <Typography variant="h6" component="p" sx={{ fontWeight: 700, color: 'primary.main' }}>
                        Ask the participant to open their secure Participant Portal on their phone and show the QR code
                        to the scanner.
                      </Typography>
                    </Box>

                    {participantErrorMessage && (
                      <Box sx={{ mt: 4, p: 3, borderRadius: 3, bgcolor: 'error.lighter' }}>
                        <Typography variant="h6" component="p" sx={{ fontWeight: 700, color: 'primary.main' }}>
                          {participantErrorMessage}
                        </Typography>
                      </Box>
                    )}

                    <Button
                      variant="contained"
                      color="primary"
                      size="large"
                      fullWidth
                      startIcon={<QrCodeScannerOutlined />}
                      onClick={handleScanParticipantQr}
                      disabled={isBusy}
                      sx={{ mt: 4, minHeight: 72, fontSize: '1.25rem', fontWeight: 700 }}
                    >
                      {isBusy ? 'Reading participant QR...' : 'Scan participant QR'}
                    </Button>
                  </Grid>
                </Grid>

                {!isBusy && participant && !participantConfirmed && (
                  <ParticipantConfirmationPanel participant={participant} onConfirm={handleConfirmParticipant} />
                )}
              </>
            )}

            {currentStep === 2 && (
              <>
                <Grid container spacing={4} alignItems="stretch">
                  <Grid item xs={12} md={4}>
                    <Box
                      sx={{
                        height: '100%',
                        minHeight: 320,
                        borderRadius: 4,
                        bgcolor: 'background.paper',
                        border: 1,
                        borderColor: 'rgba(16, 53, 68, 0.18)',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        p: 3,
                      }}
                    >
                      <SampleBarcodeIcon />
                    </Box>
                  </Grid>

                  <Grid item xs={12} md={8}>
                    <Typography variant="h3" component="h1" sx={{ fontWeight: 700, color: 'primary.main', mb: 2 }}>
                      Step 2: Link Sample Kit
                    </Typography>

                    <Typography
                      variant="h5"
                      component="p"
                      color="text.secondary"
                      sx={{ lineHeight: 1.4, maxWidth: 760 }}
                    >
                      Scan the barcode located on the sample kit provided to the participant.
                    </Typography>

                    <Box sx={{ mt: 4, p: 3, borderRadius: 3, bgcolor: 'info.lighter' }}>
                      <Typography variant="h6" component="p" sx={{ fontWeight: 700, color: 'primary.main' }}>
                        Use the barcode printed on the physical sample kit. Check that the kit is free to be linked to
                        the participant before continuing.
                      </Typography>
                    </Box>

                    {sampleScanError && (
                      <Alert severity="error" sx={{ mt: 3 }}>
                        {sampleScanError}
                      </Alert>
                    )}

                    <Button
                      variant="contained"
                      color="primary"
                      size="large"
                      fullWidth
                      startIcon={<QrCodeScannerOutlined />}
                      onClick={handleScanSampleBarcode}
                      sx={{ mt: 4, minHeight: 72, fontSize: '1.25rem', fontWeight: 700 }}
                    >
                      Scan sample kit barcode
                    </Button>
                  </Grid>
                </Grid>

                {sample && (
                  <SampleConfirmationPanel
                    confirmBtnLabel="Continue to confirmation"
                    sample={sample}
                    onContinue={handleContinueToFinalConfirmation}
                  />
                )}
              </>
            )}

            {currentStep === 3 && participant && sample && (
              <FinalConfirmationPanel
                participant={participant}
                sample={sample}
                onConfirm={handleConfirmSampleAndParticipant}
              />
            )}

            {currentStep === 4 && (
              <>
                <Grid container spacing={4} alignItems="stretch">
                  <Grid item xs={12} md={4}>
                    <Box
                      sx={{
                        height: '100%',
                        minHeight: 320,
                        borderRadius: 4,
                        bgcolor: 'background.paper',
                        border: 1,
                        borderColor: 'rgba(16, 53, 68, 0.18)',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        p: 3,
                      }}
                    >
                      <SampleBarcodeIcon />
                    </Box>
                  </Grid>

                  <Grid item xs={12} md={8}>
                    <Typography variant="h3" component="h1" sx={{ fontWeight: 700, color: 'primary.main', mb: 2 }}>
                      Step 4: Confirm Sample Collected
                    </Typography>

                    <Typography
                      variant="h5"
                      component="p"
                      color="text.secondary"
                      sx={{ lineHeight: 1.4, maxWidth: 760 }}
                    >
                      Have you collected the sample kit from the participant?
                    </Typography>

                    <Box sx={{ mt: 4, p: 3, borderRadius: 3, bgcolor: 'info.lighter' }}>
                      <Typography variant="h6" component="p" sx={{ fontWeight: 700, color: 'primary.main' }}>
                        Before linking another participant, confirm that you have collected the completed sample kit
                        from the participant by scanning the barcode printed on the sample tube.
                      </Typography>
                    </Box>

                    {sampleScanError && (
                      <Alert severity="error" sx={{ mt: 3 }}>
                        {sampleScanError}
                      </Alert>
                    )}

                    <Button
                      variant="contained"
                      color="primary"
                      size="large"
                      fullWidth
                      startIcon={<QrCodeScannerOutlined />}
                      onClick={handleScanSampleBarcode}
                      sx={{ mt: 4, minHeight: 72, fontSize: '1.25rem', fontWeight: 700 }}
                    >
                      Scan sample kit barcode
                    </Button>
                  </Grid>
                </Grid>
                {sample && secondSampleBarcode !== '' && secondSampleBarcode === sample?.sampleBarcode && (
                  <SampleConfirmationPanel
                    confirmBtnLabel="Confirm collection of sample"
                    sample={{ sampleBarcode: secondSampleBarcode, linkDate: new Date().toISOString() }}
                    onContinue={handleDoubleScanAndConfirmation}
                  />
                )}
                {sample && secondSampleBarcode !== '' && secondSampleBarcode !== sample?.sampleBarcode && (
                  <Box
                    sx={{
                      mt: 4,
                      p: 3,
                      borderRadius: 3,
                      bgcolor: theme.palette.error.lighter,
                      border: 1,
                      borderColor: 'error.main',
                    }}
                  >
                    <Typography
                      variant="h6"
                      component="p"
                      sx={{
                        fontWeight: 700,
                        color: 'primary.main',
                        mb: 1,
                      }}
                    >
                      Barcode doesn’t match
                    </Typography>

                    <Typography variant="body1" component="p" color="text.secondary" sx={{ lineHeight: 1.45 }}>
                      This barcode doesn’t match the sample kit scanned earlier. Please check you have the correct
                      sample kit and scan its barcode again.
                    </Typography>
                  </Box>
                )}
              </>
            )}

            {currentStep === 5 && participant && sample && (
              <>
                {!isBusy && !sampleLink && (
                  <LinkingFailedPanel participant={participant} sample={sample} onStartAgain={handleStartAgain} />
                )}
                {isBusy && !sampleLink && <LinkingSamplePanel participant={participant} sample={sample} />}
                {!isBusy && sampleLink && (
                  <LinkingSuccessPanel participant={participant} sample={sample} onStartAgain={handleStartAgain} />
                )}
              </>
            )}
          </Box>
        </Paper>
      </Box>
    </PortalLayout>
  );
}

export default SampleScene;
